import { useMemo } from "react";
import type { EvalState, MaturityEvaluation } from "@/types/maturity";
import {
  getMaturityColor,
  getMaturityLabel,
  getScoreCardBackground,
} from "@/utils/maturity-colors";
import { useGetScoringAlgorithm } from "@/hooks/api/query/useGetScoringAlgorithm";

type ScoreCardsProps = {
  evaluation: MaturityEvaluation;
  totalChecked?: number;
  liveScore?: number;
  keyMeasureScore?: number | null;
};

const DEFAULT_KEY_MEASURE_TARGET = 3;

const EVAL_STATE_LABEL: Partial<Record<EvalState, string>> = {
  PRELIM_COMPLETE: "Estimat",
  FULL_COMPLETE: "Evaluare completă",
};

function ScoreCards({
  evaluation,
  totalChecked,
  liveScore,
  keyMeasureScore,
}: ScoreCardsProps) {
  const { data: scoringAlgorithm } = useGetScoringAlgorithm();

  const keyMeasureTarget = useMemo(() => {
    if (!scoringAlgorithm) return DEFAULT_KEY_MEASURE_TARGET;
    const levelConfig = scoringAlgorithm.assurance_levels.find(
      (l) =>
        l.name === evaluation.assurance_level ||
        l.code === evaluation.assurance_level
    );
    return levelConfig?.key_measure_minimum ?? DEFAULT_KEY_MEASURE_TARGET;
  }, [scoringAlgorithm, evaluation.assurance_level]);

  const hasPersistedScore =
    evaluation.overall_score !== null &&
    evaluation.eval_state !== "NOT_STARTED";

  const hasInProgressScore =
    (totalChecked ?? 0) > 0 && liveScore !== undefined && liveScore > 0;

  const hasScore = hasPersistedScore || hasInProgressScore;

  const score =
    hasPersistedScore && evaluation.overall_score !== null
      ? evaluation.overall_score
      : (liveScore ?? 0);

  const isInProgressOnly = !hasPersistedScore && hasInProgressScore;

  const isEstimated =
    isInProgressOnly || evaluation.eval_state === "PRELIM_COMPLETE";
  const scoreCardTitle = isEstimated ? "Scor Estimat" : "Scor Actual";

  const showKeyMeasureCard =
    evaluation.eval_state === "FULL_COMPLETE" &&
    keyMeasureScore !== undefined &&
    keyMeasureScore !== null;

  return (
    <div className="space-y-2.5">
      <div
        className="rounded-xl px-4 py-3 text-white"
        style={{ backgroundColor: "#1A4F6E" }}
      >
        <div className="text-[10px] uppercase tracking-wider opacity-80">
          Scor Țintă
        </div>
        <div className="flex items-baseline gap-2">
          <div className="text-2xl font-heading font-bold leading-tight">
            {evaluation.target_score.toFixed(2)}
          </div>
          <div className="text-xs opacity-70">
            Nivel {evaluation.assurance_level}
          </div>
        </div>
      </div>

      <div
        className="rounded-xl px-4 py-3"
        style={{
          backgroundColor: hasScore ? getScoreCardBackground(score) : "#F8FAFC",
        }}
      >
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          {scoreCardTitle}
        </div>
        {hasScore ? (
          <div className="flex items-baseline gap-2">
            <div
              className="text-2xl font-heading font-bold leading-tight"
              style={{ color: getMaturityColor(score) }}
            >
              {score.toFixed(2)}
            </div>
            <div className="text-xs text-slate-600">
              {getMaturityLabel(score)}
            </div>
          </div>
        ) : (
          <div className="flex items-baseline gap-2">
            <div className="text-2xl font-heading font-bold text-slate-300 leading-tight">
              —
            </div>
            <div className="text-xs text-slate-400">Nicio evaluare</div>
          </div>
        )}
        {hasScore && isInProgressOnly && (
          <div className="text-[11px] text-slate-400 mt-0.5">
            Evaluare preliminară în curs
          </div>
        )}
        {hasScore &&
          !isInProgressOnly &&
          EVAL_STATE_LABEL[evaluation.eval_state] && (
            <div className="text-[11px] text-slate-400 mt-0.5">
              {EVAL_STATE_LABEL[evaluation.eval_state]}
            </div>
          )}
      </div>

      {showKeyMeasureCard && keyMeasureScore !== null && (
        <div
          className="rounded-xl px-4 py-3"
          style={{
            backgroundColor: getScoreCardBackground(keyMeasureScore),
          }}
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Scor Măsuri-Cheie
          </div>
          <div className="flex items-baseline gap-2">
            <div
              className="text-2xl font-heading font-bold leading-tight"
              style={{ color: getMaturityColor(keyMeasureScore) }}
            >
              {keyMeasureScore.toFixed(2)}
            </div>
            <div className="text-xs text-slate-600">
              {getMaturityLabel(keyMeasureScore)}
            </div>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            Prag DNSC ≥ {keyMeasureTarget.toFixed(2)}
          </div>
        </div>
      )}

      {hasScore && (
        <div className="rounded-xl border-l-4 border-blue-400 bg-blue-50 px-3 py-2">
          <p className="text-xs text-slate-700 leading-snug">
            {score >= evaluation.target_score
              ? "Organizația atinge nivelul minim de maturitate conform CyFun® 2025."
              : `Sub ținta ${evaluation.target_score.toFixed(2)}. Pentru conformitate NIS2 trimiteți la DNSC evaluarea completă și planul de remediere.`}
          </p>
        </div>
      )}
    </div>
  );
}

export default ScoreCards;
